import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share2, PlayCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { IMG } from "../lib/images";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal, SectionHeading } from "../components/motion";

const THEMES = ["foi", "prière", "famille", "espérance", "Saint-Esprit", "vie chrétienne", "Jésus-Christ", "relations", "évangélisation"];

function youtubeId(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

export default function Sermons() {
  const [sermons, setSermons] = useState([]);
  const [theme, setTheme] = useState("all");
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sermons").then(({ data }) => setSermons(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (theme === "all" ? sermons : sermons.filter((s) => s.theme === theme)),
    [sermons, theme]
  );

  const share = async (s) => {
    const url = s.youtube_url || window.location.href;
    const payload = { title: s.title, text: `${s.title} — La Maison de la Charité`, url };
    try {
      if (navigator.share) await navigator.share(payload);
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié dans le presse-papiers.");
      }
    } catch {}
  };

  return (
    <>
      <PageHeader
        testid="sermons-header"
        eyebrow="Prédications & ressources"
        titleLines={["Des messages pour", "nourrir votre foi"]}
        intro="Prédications, enseignements, résumés et documents : grandissez dans la foi à votre rythme, où que vous soyez."
        image={IMG.enseignement}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site">
          <div data-testid="sermons-filters" className="flex flex-wrap gap-2">
            <button
              data-testid="sermons-filter-all"
              onClick={() => setTheme("all")}
              className={`rounded-full px-4 py-2 text-xs font-semibold capitalize transition-colors ${theme === "all" ? "bg-obsidian text-white" : "border border-line bg-white text-stone-600 hover:border-terracotta hover:text-terracotta"}`}
            >
              Tous les thèmes
            </button>
            {THEMES.map((t) => (
              <button
                key={t}
                data-testid={`sermons-filter-${t.replace(/[^a-z-]+/gi, "-").toLowerCase()}`}
                onClick={() => setTheme(t)}
                className={`rounded-full px-4 py-2 text-xs font-semibold capitalize transition-colors ${theme === t ? "bg-obsidian text-white" : "border border-line bg-white text-stone-600 hover:border-terracotta hover:text-terracotta"}`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-10">
            {loading && <p className="text-sm text-stone-500">Chargement des messages…</p>}
            {!loading && !filtered.length && (
              <div className="card-warm border-dashed p-12 text-center">
                <BookOpen className="mx-auto h-7 w-7 text-terracotta" />
                <p className="mx-auto mt-5 max-w-md font-serif text-xl font-medium text-obsidian">
                  La bibliothèque ouvrira bientôt
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500">
                  Les messages et enseignements de La Maison de la Charité seront publiés ici
                  prochainement. Revenez vite — ou rejoignez-nous à Charity Times pour les vivre en direct.
                </p>
              </div>
            )}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s, i) => {
                const vid = youtubeId(s.youtube_url);
                return (
                  <Reveal key={s.id} delay={(i % 3) * 0.08}>
                    <article data-testid={`sermon-card-${s.id}`} className="card-warm group flex h-full flex-col overflow-hidden">
                      <div className="relative h-44 overflow-hidden bg-sand">
                        <img src={vid ? `https://i.ytimg.com/vi/${vid}/hqdefault.jpg` : s.image || IMG.books} alt={s.title} loading="lazy"
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                        {vid && (
                          <button
                            data-testid={`sermon-play-${s.id}`}
                            onClick={() => setPlaying(s)}
                            aria-label={`Écouter ${s.title}`}
                            className="absolute inset-0 flex items-center justify-center bg-obsidian/20 opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <PlayCircle className="h-14 w-14 text-white drop-shadow-lg" />
                          </button>
                        )}
                        <span className="absolute left-4 top-4 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-semibold capitalize text-terracotta backdrop-blur">
                          {s.theme}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-serif text-lg font-semibold leading-snug tracking-tight text-obsidian">{s.title}</h3>
                        {s.verse && <p className="verse-quote mt-2 !text-sm">« {s.verse} »</p>}
                        <p className="mt-2 text-xs font-medium text-stone-500">
                          {[s.preacher, s.date && format(new Date(s.date), "d MMMM yyyy", { locale: fr })].filter(Boolean).join(" · ")}
                        </p>
                        {s.summary && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-stone-600">{s.summary}</p>}
                        <div className="mt-auto flex gap-2 pt-5">
                          {vid && (
                            <button data-testid={`sermon-watch-${s.id}`} onClick={() => setPlaying(s)} className="btn-primary flex-1 !px-4 !py-2.5 !text-xs">
                              <PlayCircle className="h-4 w-4" /> Regarder
                            </button>
                          )}
                          <button data-testid={`sermon-share-${s.id}`} onClick={() => share(s)} aria-label="Partager ce message"
                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-line bg-white px-4 py-2.5 text-xs font-semibold text-obsidian transition-colors hover:border-terracotta hover:text-terracotta">
                            <Share2 className="h-4 w-4" /> Partager
                          </button>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {playing && youtubeId(playing.youtube_url) && (
          <motion.div
            data-testid="sermon-player-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/85 p-4 backdrop-blur-sm"
            onClick={() => setPlaying(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 flex items-center justify-between gap-4">
                <p className="truncate font-serif text-lg text-cream">{playing.title}</p>
                <button data-testid="sermon-player-close" onClick={() => setPlaying(null)} aria-label="Fermer le lecteur"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/10">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="aspect-video overflow-hidden rounded-2xl bg-black">
                <iframe
                  title={playing.title}
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId(playing.youtube_url)}?autoplay=1&rel=0`}
                  className="h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <a
                data-testid="sermon-open-youtube"
                href={playing.youtube_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-stone-300 underline decoration-terracotta underline-offset-4 hover:text-white"
              >
                Ouvrir sur YouTube
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
